import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchCampaign, fetchCampaigns } from "services/api/campaign";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { Campaign, CampaignDetail, CampaignReducerState } from "types/campaign";
import { getErrorMessage, isFresh } from "utils/helpers";

export const fetchAllCampaignsThunk = createAsyncThunk<{ count: number, items: Campaign[] }, { force?: boolean }, { rejectValue: string }>(
    'campaigns/fetchAllCampaigns',
    async ({ force = false }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState, campaigns: CampaignReducerState };
            const hubId = state.auth.selectedHubId;
            if (!hubId) {
                throw new Error("Hub ID is required");
            }
            const STALE_TIME = apiConfig.FETCH_CAMPAIGNS.staleTime;
            if (!force && isFresh(state.campaigns.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const { count, items } = await fetchCampaigns(hubId, { limit: 500 });
            return { count, items };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaigns!"));
        }
    }
)

export const fetchCampaignThunk = createAsyncThunk<{ campaignId: string, data: CampaignDetail }, { force?: boolean, campaignId: string }, { rejectValue: string }>(
    'campaigns/fetchCampaign',
    async ({ force = false, campaignId }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState, campaigns: CampaignReducerState };
            const STALE_TIME = apiConfig.FETCH_CAMPAIGN.staleTime;
            if (!force && isFresh(state.campaigns.campaigns[campaignId]?.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const hubId = state.auth.selectedHubId;
            if (!hubId) {
                throw new Error("Hub ID is required");
            }
            const campaign = await fetchCampaign(hubId, campaignId);
            return { data: campaign, campaignId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaign!"));
        }
    }
)